import { z } from 'zod';
import { StateCodeSchema } from './scheduling.js';
/**
 * Scheduling eligibility rules (M15, ADR-032, `Docs/specs/2026-09-23-scope-extension-design.md` §1).
 *
 * A tenant-editable decision tree that answers one question the pricing engine does not:
 * *may this patient schedule at all*, and on what footing (insured, cash, or "call the
 * office"). The client's algorithm (`Docs/_research/verification-algorithm.md`) is one
 * such tree; `DEFAULT_TREE` (always schedule) is another and is what a tenant with no
 * published ruleset runs, which is exactly the FR-INS-04 behaviour of today.
 *
 * Everything here is de-identified by construction: a state code, a handful of enums and
 * an X12 plan type code. A tree can name California; it can never name a person.
 */
/* ------------------------------------------------------------------- facts ---- */
/** The plan type as the patient declares it on the insurance step (A-4 in the spec). */
export const INSURANCE_TYPES = [
    'ppo',
    'hmo',
    'medicare',
    'medicare_advantage',
    'medicaid',
    'other',
    'self_pay',
    'unknown',
];
export const InsuranceTypeSchema = z.enum(INSURANCE_TYPES);
export const DECISION_VERIFICATIONS = ['verified', 'failed', 'unavailable', 'skipped'];
export const DECISION_COVERAGES = ['active', 'inactive', 'unknown', 'none'];
export const DECISION_NETWORKS = ['in_network', 'out_of_network', 'unknown'];
export const DECISION_PAYER_CATEGORIES = ['commercial', 'medicare', 'medicaid', 'other'];
/**
 * X12 271 insurance type codes (EB04) the builder offers by name. `none` is the value
 * the engine substitutes when the payer reported no code, so a tree can branch on it.
 * Any other two-character code is still accepted as a value: the list is the *common*
 * set, not the closed one.
 */
export const PLAN_TYPE_CODES = [
    'PR',
    'HM',
    'HN',
    'PS',
    'R',
    'QM',
    'MC',
    'MA',
    'MB',
    'C1',
    'IN',
    'OT',
    'none',
];
/** Which facts are known before a benefits check and which only after it. */
export const DECISION_PHASES = ['pre', 'post'];
export const DecisionPhaseSchema = z.enum(DECISION_PHASES);
export const DECISION_FACTS = [
    'patientState',
    'insuranceType',
    'payerCategory',
    'appointmentTypeKey',
    'verification',
    'coverage',
    'planTypeCode',
    'network',
];
export const DecisionFactSchema = z.enum(DECISION_FACTS);
/** The phase in which each fact becomes known. A `pre` walk stops at the first `post` fact. */
export const DECISION_FACT_PHASE = Object.freeze({
    patientState: 'pre',
    insuranceType: 'pre',
    payerCategory: 'pre',
    appointmentTypeKey: 'pre',
    verification: 'post',
    coverage: 'post',
    planTypeCode: 'post',
    network: 'post',
});
/**
 * Everything the engine may look at. `post` facts are optional because a `pre` walk has
 * none of them; the engine answers `verify` rather than guessing when it needs one.
 */
export const DecisionInputSchema = z
    .object({
    patientState: StateCodeSchema,
    insuranceType: InsuranceTypeSchema,
    payerCategory: z.enum(DECISION_PAYER_CATEGORIES).optional(),
    appointmentTypeKey: z.string().min(1).max(64).optional(),
    verification: z.enum(DECISION_VERIFICATIONS).optional(),
    coverage: z.enum(DECISION_COVERAGES).optional(),
    /** X12 insurance type code, upper case, or `none`. */
    planTypeCode: z
        .string()
        .regex(/^([A-Z0-9]{1,3}|none)$/)
        .optional(),
    network: z.enum(DECISION_NETWORKS).optional(),
})
    .strict();
/* ---------------------------------------------------------------- outcomes ---- */
/**
 * What a leaf decides.
 *
 * - `schedule`: continue; the pricing engine quotes as usual.
 * - `schedule_self_pay`: continue on the visit type's cash price, no verification.
 * - `contact_office`: stop; the patient is asked to call. Server-side the confirm route
 *   refuses (`SCHEDULING_BLOCKED`) and a `scheduling_blocked` review task is opened.
 *
 * `verify` is never stored in a tree: it is the engine's answer when a `pre` walk needs a
 * `post` fact.
 */
export const DECISION_ACTIONS = ['schedule', 'schedule_self_pay', 'contact_office'];
export const DecisionActionSchema = z.enum(DECISION_ACTIONS);
export const DECISION_RESULT_ACTIONS = [...DECISION_ACTIONS, 'verify'];
export const DecisionResultActionSchema = z.enum(DECISION_RESULT_ACTIONS);
/** Fixed patient-facing sentences, rendered by the apps. No free text reaches a patient. */
export const DECISION_MESSAGE_KEYS = [
    'decision.schedule.ok',
    'decision.self_pay.cash_price',
    'decision.contact_office.plan_not_accepted',
    'decision.contact_office.verification_failed',
    'decision.contact_office.no_billing_pathway',
    'decision.contact_office.generic',
];
export const DecisionMessageKeySchema = z.enum(DECISION_MESSAGE_KEYS);
/* -------------------------------------------------------------------- tree ---- */
export const DECISION_NODE_ID_PATTERN = /^[a-z0-9][a-z0-9_-]{0,63}$/;
const NodeIdSchema = z.string().regex(DECISION_NODE_ID_PATTERN);
/** One row of a switch: "when the fact is any of `values`, go to `next`". */
export const DecisionCaseSchema = z
    .object({
    id: NodeIdSchema,
    /** Builder label, e.g. "Medicare HMO codes". Never shown to patients. */
    label: z.string().max(120).default(''),
    values: z.array(z.string().min(1).max(64)).min(1).max(64),
    next: NodeIdSchema,
})
    .strict();
export const DecisionSwitchNodeSchema = z
    .object({
    id: NodeIdSchema,
    kind: z.literal('switch'),
    fact: DecisionFactSchema,
    label: z.string().max(120).default(''),
    cases: z.array(DecisionCaseSchema).min(1).max(32),
    /** Where the walk goes when no case matches. Mandatory: a tree never dangles. */
    defaultNext: NodeIdSchema,
})
    .strict();
export const DecisionOutcomeNodeSchema = z
    .object({
    id: NodeIdSchema,
    kind: z.literal('outcome'),
    action: DecisionActionSchema,
    messageKey: DecisionMessageKeySchema,
    label: z.string().max(120).default(''),
    /** Staff-facing note shown in the builder and the review task. Not for patients. */
    note: z.string().max(500).default(''),
})
    .strict();
export const DecisionNodeSchema = z.discriminatedUnion('kind', [
    DecisionSwitchNodeSchema,
    DecisionOutcomeNodeSchema,
]);
export const DECISION_TREE_MAX_NODES = 200;
export const DECISION_TREE_MAX_DEPTH = 10;
/**
 * The tree as stored and as the builder edits it. Structural rules (every `next` exists,
 * no cycles, depth, reachability, value vocabulary) are checked by
 * `validateDecisionTree` in the engine; the schema only guarantees the shape, so that
 * a half-built tree can still be simulated and saved as a draft by the UI.
 */
export const DecisionTreeSchema = z
    .object({
    /** Schema version of the tree format itself, for future migrations. */
    format: z.literal(1),
    root: NodeIdSchema,
    nodes: z.array(DecisionNodeSchema).min(1).max(DECISION_TREE_MAX_NODES),
})
    .strict();
/** A tree that always schedules: the behaviour of a tenant that never opened the builder. */
export const DEFAULT_DECISION_TREE = Object.freeze({
    format: 1,
    root: 'always_schedule',
    nodes: [
        {
            id: 'always_schedule',
            kind: 'outcome',
            action: 'schedule',
            messageKey: 'decision.schedule.ok',
            label: 'Always schedule',
            note: '',
        },
    ],
});
/* ----------------------------------------------------------------- results ---- */
export const DecisionResultSchema = z
    .object({
    action: DecisionResultActionSchema,
    /** Null only for `verify`. */
    messageKey: DecisionMessageKeySchema.nullable(),
    /** Node ids visited, root first. The simulator highlights these. */
    path: z.array(NodeIdSchema),
    /** Id of the outcome node reached, null for `verify`. */
    outcomeNodeId: NodeIdSchema.nullable(),
    /** `0` when the default tree ran. */
    rulesetVersion: z.int().nonnegative(),
    phase: DecisionPhaseSchema,
})
    .strict();
/** What is stored on `eligibility_checks.decision` and `booking_drafts.decision`. */
export const StoredDecisionSchema = DecisionResultSchema.extend({
    decidedAt: z.iso.datetime(),
}).strict();
/* ---------------------------------------------------------------- rulesets ---- */
export const RULESET_STATUSES = ['published', 'superseded'];
export const RulesetStatusSchema = z.enum(RULESET_STATUSES);
export const EligibilityRulesetSchema = z
    .object({
    id: z.uuid().nullable(),
    /** `0` is the built-in default and has no row. */
    version: z.int().nonnegative(),
    name: z.string().max(120),
    note: z.string().max(500),
    status: RulesetStatusSchema,
    tree: DecisionTreeSchema,
    publishedAt: z.iso.datetime().nullable(),
    publishedBy: z.string().max(128).nullable(),
})
    .strict();
export const EligibilityRulesetResponseSchema = z
    .object({ data: EligibilityRulesetSchema })
    .strict();
export const EligibilityRulesetSummarySchema = EligibilityRulesetSchema.omit({ tree: true })
    .extend({ nodeCount: z.int().nonnegative() })
    .strict();
export const EligibilityRulesetListResponseSchema = z
    .object({ data: z.array(EligibilityRulesetSummarySchema) })
    .strict();
export const PublishEligibilityRulesRequestSchema = z
    .object({
    name: z.string().min(1).max(120).default('Scheduling rules'),
    note: z.string().max(500).default(''),
    tree: DecisionTreeSchema,
})
    .strict();
export const SimulateDecisionRequestSchema = z
    .object({
    /** Absent means "the published ruleset". */
    tree: DecisionTreeSchema.optional(),
    input: DecisionInputSchema,
    phase: DecisionPhaseSchema.default('post'),
})
    .strict();
export const SimulateDecisionResponseSchema = z
    .object({
    data: z
        .object({
        result: DecisionResultSchema,
        /** Validation problems of the submitted tree; a problem does not stop the walk. */
        problems: z.array(z
            .object({
            nodeId: NodeIdSchema.nullable(),
            severity: z.enum(['error', 'warning']),
            message: z.string().max(300),
        })
            .strict()),
    })
        .strict(),
})
    .strict();
/* ----------------------------------------------------------------- catalog ---- */
/**
 * What the builder renders from: the facts, their values with labels, the phase each is
 * known in, the actions and message keys. Served by `GET /admin/eligibility-rules/catalog`
 * so that a new fact is a contract change, not a UI release.
 */
export const DecisionCatalogValueSchema = z
    .object({ value: z.string().max(64), label: z.string().max(120) })
    .strict();
export const DecisionCatalogFactSchema = z
    .object({
    fact: DecisionFactSchema,
    label: z.string().max(120),
    description: z.string().max(300),
    phase: DecisionPhaseSchema,
    /** `enum` renders chips from `values`; `state` renders the state multi-select; `text` a free code field with `values` as suggestions. */
    input: z.enum(['enum', 'state', 'text']),
    values: z.array(DecisionCatalogValueSchema),
})
    .strict();
export const DecisionCatalogSchema = z
    .object({
    facts: z.array(DecisionCatalogFactSchema),
    actions: z.array(z
        .object({
        action: DecisionActionSchema,
        label: z.string().max(120),
        description: z.string().max(300),
    })
        .strict()),
    messageKeys: z.array(z
        .object({
        key: DecisionMessageKeySchema,
        /** The sentence the patient will read, so the builder can show it. */
        patientText: z.string().max(400),
        /** Which actions the key makes sense for. */
        actions: z.array(DecisionActionSchema).min(1),
    })
        .strict()),
    templates: z.array(z
        .object({
        key: z.string().max(64),
        label: z.string().max(120),
        description: z.string().max(300),
        tree: DecisionTreeSchema,
    })
        .strict()),
    limits: z.object({ maxNodes: z.int().positive(), maxDepth: z.int().positive() }).strict(),
})
    .strict();
export const DecisionCatalogResponseSchema = z.object({ data: DecisionCatalogSchema }).strict();
/* ------------------------------------------------------------ public route ---- */
/** `POST /public/eligibility/decision`: the pre-phase decision for the current draft. */
export const PublicDecisionRequestSchema = z
    .object({
    insuranceType: InsuranceTypeSchema,
    payerId: z.string().max(64).optional(),
})
    .strict();
export const PublicDecisionResponseSchema = z
    .object({
    data: z
        .object({
        action: DecisionResultActionSchema,
        messageKey: DecisionMessageKeySchema.nullable(),
        rulesetVersion: z.int().nonnegative(),
    })
        .strict(),
})
    .strict();
/** The patient-facing texts, one per key. Apps render these; the builder previews them. */
export const DECISION_MESSAGE_TEXTS = Object.freeze({
    'decision.schedule.ok': 'You can continue with your booking.',
    'decision.self_pay.cash_price': 'We are not able to bill this plan directly, so this visit is offered at our self-pay price.',
    'decision.contact_office.plan_not_accepted': 'We are not able to schedule this plan online. Please contact our office and we will help you book.',
    'decision.contact_office.verification_failed': 'We could not verify your benefits online. Please contact our office and we will help you book.',
    'decision.contact_office.no_billing_pathway': 'We do not have a billing arrangement for this plan. Please contact our office to discuss your options.',
    'decision.contact_office.generic': 'Please contact our office to complete this booking.',
});
//# sourceMappingURL=eligibility-rules.js.map